import styled, { css } from 'styled-components';

// CONSTANTS
import { responsiveEndPoints } from '../../constants/responsive-endpoints';
import { theme } from '../../constants/theme';

// ENUMS
import { Themes } from '../../enums/themes';
import { TimelineItemType } from '../../enums/timeline';

export const Content = styled.div`
  display: block;
  width: 100%;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding-left: 9px;
    padding-bottom: 2px;
  }
`;

export const QtyDays = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #f1f1f1;
  border-radius: 4px;
  padding: 0px 10px 0px 8px;
  margin: 0px 8px;

  font-family: 'Clan Pro';
  font-weight: ${theme.weights.bold};
  font-size: 9px;
  line-height: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.secondary.gray200};
`;

export const TimelineStyled = styled.div`
  position: fixed;
  display: block;
  width: 327px;
  max-width: 100%;
  height: calc(100vh - 80px);
  background: ${theme.colors.primary.white};
  margin-top: 80px;
  padding: 47px 32px;
  font-weight: ${theme.weights.semibold};
  overflow: auto;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    position: relative;
    width: 100%;
    height: auto;
    margin-top: 0;
    overflow: hidden;
    padding: 0;
  }
`;

export const TimelineEmptyContainer = styled.div`
  display: block;
  width: 100%;
`;

export const TimelineEmptyItem = styled.div`
  position: relative;
  display: block;
  width: 100px;
  height: 5px;
  background: ${theme.colors.secondary.blue600};
  border-radius: 7px;
  margin-left: 15px;

  &::after {
    position: absolute;
    z-index: 1;
    content: '';
    width: 8px;
    height: 8px;
    left: -20.5px;
    top: -3px;
    background: ${theme.colors.secondary.blue500};
    border-radius: 50%;
    border: 2px solid ${theme.colors.primary.white};
  }

  &::before {
    position: absolute;
    content: '';
    width: 30px;
    height: 5px;
    top: 0;
    right: -41px;
    background: ${theme.colors.secondary.blue500};
    opacity: 0.7;
    border-radius: 7px;
  }
`;

export const TimelineEmptyStyled = styled.div`
  position: relative;
  display: block;
  margin: 61px 53px 42.5px;

  ${TimelineEmptyItem}:not(:first-child) {
    margin-top: 16px;
  }

  &::after {
    position: absolute;
    content: '';
    top: 0;
    width: 1px;
    height: 100%;
    background: ${theme.colors.secondary.blue500};
    opacity: 0.8;
  }
`;

export const TimelineEmptySubTitle = styled.h2`
  width: 218px;
  font-style: normal;
  font-weight: normal;
  font-size: 12.6px;
  line-height: 24px;
  text-align: center;
  color: ${theme.colors.secondary.gray350};
  margin: 10px auto;
`;

export const TimelineEmptyTitle = styled.h1`
  font-style: normal;
  font-weight: normal;
  font-size: 17px;
  line-height: 24px;
  text-align: center;
  letter-spacing: 0.01em;
  color: ${theme.colors.primary.black};
  margin: 10px 0px;
`;

export const Title = styled.h3`
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: ${theme.colors.secondary.gray300};
`;

export const TimelineContentItemStyled = styled.div`
  position: relative;
  display: block;
  padding-left: 6px;
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: ${theme.colors.primary.black};
`;

export const TimelineLoadingStyled = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  padding-left: 12px;
`;
export const TimelineContentStyled = styled.div<{ isLoading?: boolean }>`
  position: relative;
  display: block;
  margin-top: 32px;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    margin-top: 24px;
  }

  & ${TimelineContentItemStyled}:not(:first-child) {
    margin-top: 32px;
  }

  & ${TimelineLoadingStyled}:not(:first-child) {
    margin-top: 32px;
  }

  &::after {
    position: absolute;
    content: '';
    top: 7px;
    width: 1px;
    height: ${({ isLoading }) => (!isLoading ? '99%' : '90%')};
    background: ${({ isLoading }) =>
      !isLoading ? theme.colors.secondary.gray500 : theme.colors.primary.transparentBorder};
  }
`;

export const TimelineContentItemHeaderStyled = styled.div<{
  isExpanded?: boolean;
  isExpandItem?: boolean;
  isHeader?: boolean;
  isOpened: boolean;
  type: TimelineItemType;
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  ${({ isHeader }) =>
    !isHeader &&
    css`
      margin: 16px 0;
    `}

  ${({ isHeader, type }) =>
    (isHeader || type === TimelineItemType.INIT) &&
    css`
      margin-left: 6px;
      justify-content: initial;
    `}

  ${({ isExpandItem }) =>
    isExpandItem &&
    css`
      padding-left: 8px;
      font-size: 12px;
    `}

  &::after {
    position: absolute;
    content: '';
    text-align: center;
    z-index: 1;
    top: 0;
    bottom: 0;
    margin: auto;

    transition: all 0.2s ease-out;

    ${({ isHeader, type }) =>
      !isHeader &&
      type === TimelineItemType.CURRENT &&
      css`
        left: -15.5px;
        width: 8px;
        height: 8px;
        background: ${theme.colors.primary.black};
        border-radius: 1.5px;
        transform: rotate(45deg);
      `}

    ${({ isHeader, type }) =>
      !isHeader &&
      type === TimelineItemType.DEFAULT &&
      css`
        left: -15px;
        width: 7px;
        height: 7px;
        background: ${theme.colors.primary.white};
        border: 1px solid ${theme.colors.secondary.gray400};
        box-sizing: border-box;
        border-radius: 50%;
      `}

    ${({ isHeader, type }) =>
      isHeader &&
      type === TimelineItemType.CURRENT &&
      css`
        left: -15.5px;
        width: 8px;
        height: 8px;
        background: #b7b7b7;
        border-radius: 50%;
      `}

    ${({ isHeader, type }) =>
      isHeader &&
      type === TimelineItemType.DEFAULT &&
      css`
        left: -15px;
        width: 8px;
        height: 8px;
        background: ${theme.colors.primary.white};
        border: 1px solid ${theme.colors.secondary.gray400};
        box-sizing: border-box;
        border-radius: 50%;
      `}

    ${({ type }) =>
      type === TimelineItemType.INIT &&
      css`
        left: -17px;
        width: 0;
        height: 0;
        border-left: 5.5px solid transparent;
        border-right: 5.5px solid transparent;
        border-bottom: 9px solid ${theme.colors.primary.black};
      `}

    ${({ isExpanded, type, isHeader }) =>
      type === TimelineItemType.EXPAND &&
      !isHeader &&
      css`
        ${isExpanded
          ? `
        content: '-';
        `
          : `
        content: '+';
        `};

        font-size: 20px;
        border: none;
        color: ${theme.colors.primary.white};
        line-height: 20px;
        left: -21px;
        width: 20px;
        height: 20px;
        background: ${theme.colors.primary.black};
        border-radius: 50%;
      `}


    ${({ isHeader, isOpened, type }) =>
      !isHeader &&
      isOpened &&
      type === TimelineItemType.DEFAULT &&
      css`
        content: '-';
        border: none;
        color: ${theme.colors.primary.white};
        line-height: 11px;
        left: -17px;
        width: 11px;
        height: 11px;
        background: ${theme.colors.primary.black};
        line-height: 11px;
        border-radius: 50%;
      `}
  }

  &:hover {
    &::after {
      ${({ isHeader, isOpened, type }) =>
        type === TimelineItemType.DEFAULT &&
        isHeader &&
        !isOpened &&
        css`
          content: '+';
          border: none;
          color: ${theme.colors.primary.white};
          line-height: 11px;
          left: -17px;
          width: 11px;
          height: 11px;
          background: ${theme.colors.primary.black};
          line-height: 11px;
          border-radius: 50%;
        `}
    }
  }
`;

export const TimelineContentContainerStyled = styled.div<{ isOpened: boolean }>`
  display: none;
  transition: all 0.25s ease-in-out;
  height: 0;

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    padding-top: 4px;
  }

  ${({ isOpened }) =>
    isOpened &&
    css`
      display: block;
      height: auto;
    `}
`;

export const TimelineContentItemContainerStyled = styled.div<{ color?: Themes; isFinishedTask?: boolean }>`
  display: block;
  font-weight: ${theme.weights.regular};
  border-radius: 4px;
  padding: 12px;
  margin-top: 12px;

  border: 1px solid ${theme.colors.secondary.gray500};

  ${({ isFinishedTask }) =>
    isFinishedTask &&
    css`
      display: flex;
      justify-content: space-between;
      align-items: center;

      span {
        color: ${theme.colors.secondary.gray100};
        font-weight: 700;
        background-color: ${theme.colors.secondary.gray500};
        padding: 2px 8px;
        border-radius: 2px;
      }
    `}

  ${({ color }) =>
    color == Themes.DEFAULT &&
    css`
      border: 1px solid ${theme.colors.secondary.gray500};
      background: ${theme.colors.secondary.gray600};
    `}

  ${({ color }) =>
    color == Themes.WARNING &&
    css`
      border: 1px solid ${theme.colors.secondary.orange500};
      background: ${theme.colors.secondary.orange600};
    `}

  ${({ color }) =>
    color == Themes.PRIMARY &&
    css`
      border: 1px solid ${theme.colors.secondary.blue500};
      background: ${theme.colors.secondary.blue600};
    `}
  
  ${({ color }) =>
    color == Themes.ALERT &&
    css`
      border: 1px solid ${theme.colors.secondary.red500};
      background: ${theme.colors.secondary.red600};
    `}
    
  ${({ color }) =>
    color == Themes.SECONDARY &&
    css`
      border: 1px solid ${theme.colors.secondary.green500};
      background: ${theme.colors.secondary.green600};
    `}

  ${({ color }) =>
    color == Themes.INFO &&
    css`
      border: 1px solid ${theme.colors.secondary.purple500};
      background: ${theme.colors.secondary.purple600};
    `}
`;

export const TimelineContentItemContainerHeaderStyled = styled.div`
  display: block;
  margin-bottom: 4px;
`;

export const TimelineTitle = styled.span`
  font-weight: ${theme.weights.semibold};
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: ${theme.colors.secondary.black};
`;

export const TimelineSubTitle = styled.span`
  font-weight: ${theme.weights.semibold};
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: ${theme.colors.secondary.black};
  opacity: 0.5;
`;

export const TilelineMessage = styled.p<{ isDisabledAction: boolean }>`
  font-size: 11px;
  line-height: 164%;
  letter-spacing: 0.01em;
  color: ${theme.colors.secondary.gray100};

  svg {
    width: 18px;
    transform: rotateX(180deg);
    fill: ${theme.colors.secondary.gray100};
    cursor: pointer;
  }

  a {
    font-weight: ${theme.weights.semibold};
    ${({ isDisabledAction }) =>
      !isDisabledAction
        ? css`
            color: ${theme.colors.secondary.blue200};
            cursor: pointer;
          `
        : css`
            color: ${theme.colors.states.disabled};
            cursor: not-allowed;
          `}
  }
`;

export const TimelineFooterStyled = styled.div`
  margin-top: 18px;
  margin-bottom: 6px;
`;
export const TimelineFooterBadgeStyled = styled.span`
  font-weight: ${theme.weights.bold};
  text-transform: uppercase;
  text-align: center;
  font-weight: bold;
  font-size: 9px;
  line-height: 16px;
  letter-spacing: 0.04em;
  color: ${theme.colors.secondary.gray600};

  align-items: center;
  padding: 6px 6px;

  background: ${theme.colors.primary.black};
  border-radius: 4px;
`;

export const TimelineFooterInfoStyled = styled.div`
  display: block;
  margin-left: 8px;
`;

export const TimelineFooterTitleStyled = styled.span`
  font-weight: ${theme.weights.bold};
  font-size: 10px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: ${theme.colors.primary.black};
`;
export const TimelineFooterSubTitleStyled = styled.span`
  font-weight: ${theme.weights.semibold};
  font-size: 9px;
  line-height: 16px;
  color: ${theme.colors.secondary.gray200};
`;
